const API_URL = process.env.NEXT_PUBLIC_API_URL || ''

/** Backend yanıt vermezse istek bu süre sonunda iptal edilir (ms). */
export const API_TIMEOUT_MS = 8000

export interface FetchOptions {
  /** Sunucu tarafı ISR süresi (saniye). `noStore: true` ile birlikte kullanılmaz. */
  revalidate?: number
  /** Canlı veri: önbelleğe alma, her seferinde backend'e git. */
  noStore?: boolean
  /** Bu istek için özel zaman aşımı (ms). */
  timeoutMs?: number
  /** Çağıranın kendi iptal sinyali (bileşen unmount olunca isteği düşürmek için). */
  signal?: AbortSignal
}

/**
 * Backend'e giden TÜM istekler buradan geçer.
 *
 * Ağustos 2026 denetimi: eskiden hiçbir fetch'te zaman aşımı yoktu. Backend TCP
 * bağlantıyı kabul edip yanıt vermediğinde istek undici'nin 300 saniyelik header
 * timeout'una kadar asılı kalıyordu; kök locale layout tüm rotalarda ortak olduğu
 * için dinamik render edilen her sayfa aynı gecikmeyi yiyordu.
 */
export async function fetchFromApi<T>(path: string, options?: FetchOptions): Promise<T | null> {
  const timeoutMs = options?.timeoutMs ?? API_TIMEOUT_MS

  // Çağıranın sinyali varsa zaman aşımıyla birleştir: hangisi önce gelirse istek düşer.
  const timeoutSignal = AbortSignal.timeout(timeoutMs)
  const signal = options?.signal
    ? AbortSignal.any([options.signal, timeoutSignal])
    : timeoutSignal

  try {
    const res = await fetch(`${API_URL}${path}`, {
      signal,
      ...(options?.noStore
        ? { cache: 'no-store' as const }
        : { next: { revalidate: options?.revalidate ?? 60 } }),
    })
    if (!res.ok) return null
    return (await res.json()) as T
  } catch {
    // Zaman aşımı, ağ hatası ve iptal aynı şekilde ele alınır: "veri yok".
    return null
  }
}

/**
 * `fetchFromApi` başarısızlık nedenini ayırt etmez; hem "kayıt yok" (404) hem
 * "backend erişilemiyor" (timeout / 5xx / ağ hatası) için null döner.
 *
 * Bu ayrım sunucu tarafı varlık doğrulamasında KRİTİKTİR: bir API kesintisinde
 * notFound() çağırmak, aksi hâlde geçerli olan binlerce sayfayı Google'a 404
 * olarak sunar ve indeksten düşürür. Böyle yerlerde bu sürümü kullanın:
 * yalnızca `reason === 'notfound'` geldiğinde 404 üretin.
 */
export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; reason: 'notfound' | 'unavailable' }

export async function fetchFromApiResult<T>(path: string, options?: FetchOptions): Promise<ApiResult<T>> {
  const timeoutMs = options?.timeoutMs ?? API_TIMEOUT_MS
  const timeoutSignal = AbortSignal.timeout(timeoutMs)
  const signal = options?.signal ? AbortSignal.any([options.signal, timeoutSignal]) : timeoutSignal

  try {
    const res = await fetch(`${API_URL}${path}`, {
      signal,
      ...(options?.noStore
        ? { cache: 'no-store' as const }
        : { next: { revalidate: options?.revalidate ?? 60 } }),
    })

    // Yalnızca 404/410 "bu kayıt yok" demektir. 5xx, 429 ve ağ hataları geçicidir.
    if (res.status === 404 || res.status === 410) return { ok: false, reason: 'notfound' }
    if (!res.ok) return { ok: false, reason: 'unavailable' }

    return { ok: true, data: (await res.json()) as T }
  } catch {
    return { ok: false, reason: 'unavailable' }
  }
}

/** Mutlak API adresi gerektiğinde (ör. <img src>) kullanılır. */
export function apiUrl(path: string): string {
  return `${API_URL}${path}`
}
